import { buildInfo } from '../config/buildInfo';

export type VersionCheckResult =
  | { action: 'prompt_update'; serverBuild: number; clientBuild: number }
  | { action: 'silent_activate'; serverBuild: number; clientBuild: number }
  | { action: 'no_action'; serverBuild: number | null; clientBuild: number };

const BUILD_JSON_URL = `${process.env.PUBLIC_URL || ''}/build.json`;
const SW_URL = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

function parseBuildFromJson(text: string): number | null {
  try {
    const data = JSON.parse(text) as { buildNumber?: number };
    const n = data.buildNumber;
    return typeof n === 'number' && !Number.isNaN(n) ? n : null;
  } catch {
    return null;
  }
}

function parseBuildFromServiceWorker(text: string): number | null {
  const match = text.match(/const\s+APP_BUILD\s*=\s*(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/** Build number embebido en el bundle actual. */
export function getClientBuildNumber(): number {
  return buildInfo.buildNumber;
}

/**
 * Obtiene el build publicado en el servidor (sin caché del navegador).
 * Primero intenta build.json; si falla, parsea service-worker.js.
 */
export async function fetchServerBuildNumber(): Promise<number | null> {
  const cacheBust = `t=${Date.now()}`;

  try {
    const res = await fetch(`${BUILD_JSON_URL}?${cacheBust}`, {
      cache: 'no-store',
    });
    if (res.ok) {
      const text = await res.text();
      const build = parseBuildFromJson(text);
      if (build != null) {
        return build;
      }
    }
  } catch {
    // fallback al SW
  }

  try {
    const res = await fetch(`${SW_URL}?${cacheBust}`, { cache: 'no-store' });
    if (res.ok) {
      const text = await res.text();
      return parseBuildFromServiceWorker(text);
    }
  } catch {
    // sin versión remota
  }

  return null;
}

/**
 * Decide si mostrar el aviso de actualización o activar el SW en espera en silencio.
 */
export async function resolveUpdateAction(): Promise<VersionCheckResult> {
  const clientBuild = getClientBuildNumber();
  const serverBuild = await fetchServerBuildNumber();

  if (serverBuild == null) {
    return { action: 'no_action', serverBuild: null, clientBuild };
  }

  if (serverBuild > clientBuild) {
    return { action: 'prompt_update', serverBuild, clientBuild };
  }

  if (serverBuild <= clientBuild) {
    return { action: 'silent_activate', serverBuild, clientBuild };
  }

  return { action: 'no_action', serverBuild, clientBuild };
}

/** Alias de resolveUpdateAction para chequeos periódicos. */
export async function checkForNewBuild(): Promise<VersionCheckResult> {
  return resolveUpdateAction();
}

/** Activa un service worker en estado waiting (skipWaiting). */
export function activateWaitingWorker(
  registration: ServiceWorkerRegistration
): boolean {
  if (!registration.waiting) {
    return false;
  }
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

/** Recarga la página una vez cuando el controller del SW cambia. */
export function reloadOnControllerChange(fallbackMs = 500): void {
  const reloadOnce = () => {
    window.location.reload();
  };
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, {
      once: true,
    });
  }
  setTimeout(reloadOnce, fallbackMs);
}

/**
 * Recarga dura: activa SW en espera, desregistra workers, limpia caches
 * y navega con cache-bust (equivalente práctico a Ctrl+F5).
 */
export async function hardReloadApp(
  registration?: ServiceWorkerRegistration | null
): Promise<void> {
  let reg = registration ?? null;
  if (!reg?.waiting && 'serviceWorker' in navigator) {
    reg = (await navigator.serviceWorker.getRegistration()) ?? null;
  }

  if (reg?.waiting) {
    activateWaitingWorker(reg);
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  } catch (error) {
    console.error('Error al limpiar cache:', error);
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_', String(Date.now()));
  window.location.replace(url.toString());
}
